import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { TeacherDashboardData } from '@/types/models';
import { dashboardStyles } from '@/layouts/TeacherLayout';
import { format } from 'date-fns';

interface ApprovalsTabProps {
  data: TeacherDashboardData;
}

interface PendingLog {
  id: number;
  student: {
    id: number;
    name: string;
    email: string;
  };
  opportunity: {
    id: number;
    title: string;
    organization: string;
    commitment_hours: number;
    location_type: string;
  };
  hours: number;
  description: string;
  activity_date: string;
  created_at: string;
}

const ApprovalsTab: React.FC<ApprovalsTabProps> = ({ data }) => {
  const [selectedLog, setSelectedLog] = useState<PendingLog | null>(null);
  const theme = useTheme();

  const handleApprove = (logId: number) => {
    // TODO: Implement approval logic
    console.log('Approving log:', logId);
  };

  const handleReject = (logId: number) => {
    // TODO: Implement rejection logic
    console.log('Rejecting log:', logId);
  };

  const ApprovalCard: React.FC<{ log: PendingLog }> = ({ log }) => (
    <Paper 
      sx={{
        ...dashboardStyles.dashboard.analytics.card,
        p: 0,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        p: 2, 
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.primary.main, 0.05)
      }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs>
            <Typography variant="subtitle1" fontWeight="medium">
              {log.student.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {log.student.email}
            </Typography>
          </Grid>
          <Grid item>
            <Chip
              label={`${log.hours} hours`}
              color="primary"
              size="small"
              sx={{ fontWeight: 'medium' }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {log.opportunity.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {log.opportunity.organization}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          mt: 2,
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {format(new Date(log.activity_date), 'MMM d, yyyy')}
            </Typography>
          </Box>
          <Chip
            label={log.opportunity.location_type}
            size="small"
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleApprove(log.id)}
            size="small"
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => handleReject(log.id)}
            size="small"
          >
            Reject
          </Button>
          <IconButton 
            size="small"
            onClick={() => setSelectedLog(log)}
            sx={{ ml: 'auto' }}
          >
            <InfoIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          Pending Approvals
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {data.class_stats.pending_approvals} volunteer logs waiting for review
        </Typography>
      </Box>

      {/* Approvals Grid */}
      <Grid container spacing={3}>
        {data.pending_approvals.map(log => (
          <Grid item xs={12} md={6} lg={4} key={log.id}>
            <ApprovalCard log={log} />
          </Grid>
        ))}
      </Grid>

      {/* Details Dialog */}
      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedLog && (
          <>
            <DialogTitle>
              <Typography variant="h6">
                Volunteer Log Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submitted on {format(new Date(selectedLog.created_at), 'MMM d, yyyy')}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Student
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.student.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedLog.student.email}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Opportunity
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.opportunity.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedLog.opportunity.organization}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Details
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Hours Logged
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.hours} hours
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Activity Date
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(selectedLog.activity_date), 'MMMM d, yyyy')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.description}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleReject(selectedLog.id)}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApprove(selectedLog.id)}
              >
                Approve
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ApprovalsTab; 